import { useCallback } from 'react'
import { createEntityHooks } from '../core/createEntityHooks'
import { useRxDB } from '../core/useRxDB'
import type { BaseDocument } from '../core/types'
import useLoginStore from '@/store/LoginStore'

/**
 * PersonalNote document interface
 */
export interface PersonalNoteDocument extends BaseDocument {
  userId: string
  noteType: string // 'daily' | 'weekly'
  noteDate: string // "2026-03-29" for daily, "2026-W13" for weekly
  title: string
}

/**
 * PersonalNote-specific hooks
 */
const hooks = createEntityHooks<PersonalNoteDocument>('personalnotes')

export const usePersonalNotes = (noteType?: 'daily' | 'weekly') => {
  const envId = useLoginStore((state) => state.workspaceId)
  const userId = useLoginStore((state) => state.userId)
  const enabled = !!(envId && userId)
  const { data, isLoading, db } = hooks.useEntities(
    enabled
      ? noteType
        ? { envId: envId!, userId: userId!, noteType, isDeleted: false }
        : { envId: envId!, userId: userId!, isDeleted: false }
      : undefined,
    { enabled },
  )
  return { notes: data, isLoading, db }
}

export const usePersonalNote = (id: string | undefined) => {
  const { data, isLoading } = hooks.useEntity(id)
  return { note: data, isLoading }
}

export const usePersonalNoteByDate = (
  noteType: 'daily' | 'weekly',
  noteDate: string | undefined,
) => {
  const envId = useLoginStore((state) => state.workspaceId)
  const userId = useLoginStore((state) => state.userId)
  const enabled = !!(envId && userId && noteDate)
  const { data, isLoading } = hooks.useEntities(
    enabled
      ? { envId: envId!, userId: userId!, noteType, noteDate: noteDate!, isDeleted: false }
      : undefined,
    { enabled },
  )
  return { note: data[0] || null, isLoading }
}

export const usePersonalNoteMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  return {
    createPersonalNote: create,
    updatePersonalNote: update,
    deletePersonalNote: deleteEntity,
  }
}

/**
 * Returns a direct, non-reactive lookup against the personalnotes collection
 * by natural key `(envId, userId, noteType, noteDate)`. Used to guard against
 * race conditions where a same-date note is replicated in between the
 * reactive hook's last emission and the user's first keystroke.
 */
export const usePersonalNoteNaturalKeyLookup = () => {
  const { db } = useRxDB()
  return useCallback(
    async (
      envId: string,
      userId: string,
      noteType: string,
      noteDate: string,
    ): Promise<PersonalNoteDocument | null> => {
      if (!db) return null
      const doc = await (db as any).personalnotes
        .findOne({
          selector: { envId, userId, noteType, noteDate, isDeleted: false },
        })
        .exec()
      return doc ? (doc.toJSON() as PersonalNoteDocument) : null
    },
    [db],
  )
}
